import { api } from '../core';
import { BUNDLE_SECTIONS_TAG, BUNDLE_UNDO_REDO_HISTORY_TAG } from '../constants';
import { RootState, setBundlePageIds, setBundleSections, setViewportPageIdForRestorePageIndexInStore } from '@/store';
import { flattenPageIds, replaceNullKeysAndFieldsInTree, SectionsTreeNode } from '@repo/atomic-components';
import { format } from 'date-fns';
import {
  FILTER_BLANKS_KEY,
  FILTER_DUPLICATES_KEY,
  FILTER_EXTRACTED_KEY,
  FILTER_NON_BLANKS_KEY,
  PROVIDERS_FILTER_GROUP_NAME,
} from '@/constants/filters';

export type BundleSectionsTreeNode = SectionsTreeNode;

type BundleSectionsResponse = BundleSectionsTreeNode[];

export type GetBundleSectionsProps = {
  bundleId: string;
  /** yyyy-MM-dd */
  startDate?: string;
  /** yyyy-MM-dd */
  endDate?: string;
  textLabelIds?: string[];
  pageLabelIds?: string[];
  providerIds?: string[];
  filterBlanks?: boolean;
  filterNonBlanks?: boolean;
  filterExtracted?: boolean;
  filterDuplicates?: boolean;
  abortController?: AbortController;
};

export const getCurrentBundleSectionsQueryArgs = (state: RootState, bundleId: string): GetBundleSectionsProps => {
  const activeBundleType = state.sharedData.activeBundleType;

  if (!activeBundleType) {
    return { bundleId };
  }

  const { bundleFilterOptions, enabledStates, selectedValues } = state.bundleFilterOptions[activeBundleType];

  return Object.entries(selectedValues).reduce(
    (acc: GetBundleSectionsProps, [key, value]) => {
      if (key === 'dates' && Array.isArray(value) && value.length && enabledStates[key]) {
        const [startDate, endDate] = value;

        return {
          ...acc,
          ...(startDate && { startDate: format(startDate, 'yyyy-MM-dd') }),
          ...(endDate && { endDate: format(endDate, 'yyyy-MM-dd') }),
        };
      }

      if (key === PROVIDERS_FILTER_GROUP_NAME && Array.isArray(value) && value.length && enabledStates[key]) {
        return {
          ...acc,
          providerIds: [...(acc.providerIds || []), ...value.filter((id): id is string => typeof id === 'string')],
        };
      }

      if (
        bundleFilterOptions.find((item) => item.groupName === key && item.groupName !== PROVIDERS_FILTER_GROUP_NAME)
          ?.displayType === 'checkbox' &&
        Array.isArray(value) &&
        value.length
      ) {
        return {
          ...acc,
          pageLabelIds: [...(acc.pageLabelIds || []), ...value.filter((id): id is string => typeof id === 'string')],
        };
      }

      if (key !== 'dates' && Array.isArray(value) && value.length && enabledStates[key]) {
        return {
          ...acc,
          textLabelIds: [...(acc.textLabelIds || []), ...value.filter((id): id is string => typeof id === 'string')],
        };
      }

      return acc;
    },
    {
      bundleId,
      filterBlanks: enabledStates[FILTER_NON_BLANKS_KEY] ? false : !enabledStates[FILTER_BLANKS_KEY],
      filterNonBlanks: !!enabledStates[FILTER_NON_BLANKS_KEY],
      filterExtracted: !!enabledStates[FILTER_EXTRACTED_KEY],
      filterDuplicates: !!enabledStates[FILTER_DUPLICATES_KEY],
    },
  );
};

export const refreshBundleSectionsWithCurrentFilters = (dispatch: (action: unknown) => unknown, state: RootState) => {
  const bundleId = state.sharedData.bundleId;

  if (!bundleId) {
    return;
  }

  dispatch(
    getBundleSectionsApi.endpoints.getBundleSections.initiate(getCurrentBundleSectionsQueryArgs(state, bundleId), {
      forceRefetch: true,
      subscribe: false,
    }),
  );
  dispatch(api.util.invalidateTags([{ type: BUNDLE_UNDO_REDO_HISTORY_TAG, id: bundleId }]));
};

export const getBundleSectionsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getBundleSections: builder.query<BundleSectionsResponse, GetBundleSectionsProps>({
      query: ({ bundleId, abortController = new AbortController(), ...otherData }: GetBundleSectionsProps) => ({
        url: 'bundle/sections',
        method: 'POST',
        signal: abortController.signal,
        body: { bundle_id: bundleId, ...otherData },
      }),
      providesTags: (_result, _err, { bundleId }) => [{ type: BUNDLE_SECTIONS_TAG, id: bundleId }],
      async onQueryStarted(_arg, { dispatch, queryFulfilled, getState }) {
        try {
          const { data } = await queryFulfilled;
          const state = getState() as RootState;

          if (data && state.sharedData.activeBundleType && state.sharedData.activeBundleType) {
            const newBundleSections = replaceNullKeysAndFieldsInTree(data);
            dispatch(
              setBundlePageIds({
                key: state.sharedData.activeBundleType,
                data: flattenPageIds(newBundleSections),
              }),
            );
            dispatch(
              setBundleSections({
                key: state.sharedData.activeBundleType,
                data: newBundleSections,
              }),
            );

            if (state.bundleViewportData.viewportPageIdForRestorePageIndexInStore) {
              dispatch(setViewportPageIdForRestorePageIndexInStore(null));
            }
          }
        } catch (error) {
          if (error instanceof Error && error.name === 'AbortError') {
            console.log('Request was aborted');
          } else {
            console.error('Error in getBundleSections in onQueryStarted:', error);
          }
        }
      },
    }),
  }),
});

/**
 * Export hooks for usage in functional components, which are
 * auto-generated based on the defined endpoints
 */
export const { useLazyGetBundleSectionsQuery } = getBundleSectionsApi;
